import { toast } from 'sonner';
import { useSelector } from 'react-redux';

// Tied property per signal (source, type) - must be entered and not empty for Design step
const SIGNAL_TIED_PROPERTIES = {
  web: {
    page_view: { key: 'url', label: 'Page URL' },
    button_click: { key: 'elementId', label: 'Button element ID' },
    form_submit: [
      { key: 'formId', label: 'Form ID' },
      { key: 'emailInputId', label: 'Email input ID' },
    ],
    time_spent: [
      { key: 'url', label: 'Page URL' },
      { key: 'durationS', label: 'Minimum time on page (s)' },
    ],
  },
};

const getTiedProperty = (source, type) => {
  const bySource = SIGNAL_TIED_PROPERTIES[source];
  return bySource?.[type] ?? null;
};

const isTiedPropertyEmpty = (value, key) => {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return String(value).trim() === '';
  if (key === 'durationS') return !Number.isFinite(value) || value < 0;
  return false;
};

const useQualificationValidator = () => {
  const agentTitle = useSelector(state => state.agent.qualificationAgent.title);

  const domainConfig = useSelector(state => state.agent.qualificationAgent.domainConfig);

  const designStages = useSelector(state => state.agent.qualificationAgent.stages);

  const signals = useSelector(state => state.agent.qualificationAgent.signals);

  const isScriptAdded = useSelector(state => state.agent.etc.isScriptAdded);

  const isQualificationDefineNextButtonDisabled = () => {
    if (!isScriptAdded) {
      toast.warning('Confirm that the Bridged tracking script is on your website');
      return true;
    }

    if (!agentTitle) {
      toast.warning('Enter a name for this agent');

      return true;
    }

    if (domainConfig?.type === 'fullDomain' && (domainConfig?.domainId ?? '') === '') {
      toast.warning('Select a domain');
      return true;
    }

    if (domainConfig?.type === 'sectionOfDomain' && !domainConfig?.paths?.length) {
      toast.warning('Add at least one path');
      return true;
    }

    if (domainConfig?.type === 'singlePage' && !domainConfig?.paths?.length) {
      toast.warning('Add at least one page');
      return true;
    }

    if (domainConfig?.type === 'regexSectionOfDomain' && !domainConfig?.paths?.length) {
      toast.warning('Add at least one regex pattern');
      return true;
    }

    return false;
  };

  const isQualificationDesignNextButtonDisabled = () => {
    const signalConfigs = Array.isArray(signals) ? signals : [];

    if (signalConfigs.length === 0) {
      toast.warning('Add at least one signal');
      return true;
    }

    for (let i = 0; i < signalConfigs.length; i++) {
      const config = signalConfigs[i];
      const signalData = config?.signalData || {};
      const source = signalData.source;
      const type = signalData.type;
      const tied = getTiedProperty(source, type);

      if (!tied) continue;

      const requiredFields = Array.isArray(tied) ? tied : [tied];
      for (let j = 0; j < requiredFields.length; j++) {
        const field = requiredFields[j];
        const value = signalData[field.key];
        if (isTiedPropertyEmpty(value, field.key)) {
          toast.warning('Fill in the required fields for each signal');
          return true;
        }
      }
    }

    const stageList = Array.isArray(designStages) ? designStages : [];
    if (stageList.length === 0) {
      toast.warning('Add at least one stage');
      return true;
    }

    for (let i = 0; i < stageList.length; i++) {
      const stage = stageList[i];
      const title = stage?.title ?? stage?.name ?? '';
      const score = stage?.score;
      if (typeof title !== 'string' || title.trim() === '') {
        toast.warning('Name each stage');
        return true;
      }
      if (
        score === undefined ||
        score === null ||
        !Number.isFinite(Number(score)) ||
        Number(score) < 0
      ) {
        toast.warning('Set a minimum score for each stage');
        return true;
      }
    }

    return false;
  };

  return { isQualificationDefineNextButtonDisabled, isQualificationDesignNextButtonDisabled };
};

export default useQualificationValidator;
